import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

export const inputBaseClass =
  'w-full rounded-lg border border-line bg-surface px-3 text-sm text-fg placeholder:text-muted ' +
  'transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40 ' +
  'disabled:cursor-not-allowed disabled:opacity-60 read-only:bg-elevated';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
  leftIcon?: ReactNode;
  suffix?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid, leftIcon, suffix, ...rest },
  ref,
) {
  const input = (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        inputBaseClass,
        'h-10',
        leftIcon && 'pl-9',
        suffix && 'pr-12',
        invalid && 'border-danger focus:border-danger focus:ring-danger/40',
        className,
      )}
      {...rest}
    />
  );

  if (!leftIcon && !suffix) return input;

  return (
    <div className="relative">
      {leftIcon && (
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" aria-hidden>
          {leftIcon}
        </span>
      )}
      {input}
      {suffix && (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted">
          {suffix}
        </span>
      )}
    </div>
  );
});
